/**
 * Globo Mega Menu – mobile AliExpress/tabs fix
 *
 * Shop uses gm-submenu-aliexpress: tab labels live in .gm-tab-links and
 * bodies in sibling .gm-tab-contents, so on mobile the open panel renders
 * after every label. This script moves the active panel under its label
 * and normalizes column/image layout so content is readable.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 768px)');
  const originalHomes = new WeakMap();
  let syncScheduled = false;

  function isMobile() {
    return MOBILE_MQ.matches || !!document.querySelector('.gm-menu.gm-menu-mobile');
  }

  function getTabsRoots() {
    return document.querySelectorAll('.gm-submenu.gm-aliexpress .gm-tabs');
  }

  function rememberHome(panel) {
    if (!originalHomes.has(panel)) {
      originalHomes.set(panel, {
        parent: panel.parentElement,
        next: panel.nextElementSibling,
      });
    }
  }

  function restorePanel(panel) {
    if (!panel) return;
    panel.classList.remove('gm-tab-content--mobile-nested');

    const home = originalHomes.get(panel);
    if (!home || !home.parent || !document.contains(home.parent)) return;
    if (panel.parentElement === home.parent) return;

    if (home.next && home.next.parentElement === home.parent) {
      home.parent.insertBefore(panel, home.next);
    } else {
      home.parent.appendChild(panel);
    }
  }

  function restoreAllPanels(tabsRoot) {
    const scope = tabsRoot || document;
    scope
      .querySelectorAll('.gm-submenu.gm-aliexpress .gm-tab-content, .gm-tab-links > .gm-tab-content')
      .forEach(restorePanel);
  }

  function placePanelAfterLink(linkItem, panel) {
    if (!linkItem || !panel) return;
    rememberHome(panel);

    if (panel.previousElementSibling !== linkItem || panel.parentElement !== linkItem.parentElement) {
      linkItem.insertAdjacentElement('afterend', panel);
    }

    panel.classList.add('gm-tab-content--mobile-nested');
  }

  function findActiveLink(links) {
    return (
      links.querySelector('.gm-item.gm-active[data-tab-index]') ||
      links.querySelector('.gm-item.gm-visible.gm-active[data-tab-index]')
    );
  }

  function findActivePanel(tabsRoot, contents, preferredIndex) {
    if (preferredIndex != null) {
      const byIndex =
        contents.querySelector(`.gm-tab-content[data-tab-index="${preferredIndex}"]`) ||
        tabsRoot.querySelector(`.gm-tab-content[data-tab-index="${preferredIndex}"]`) ||
        tabsRoot.querySelector(`.gm-tab-links > .gm-tab-content[data-tab-index="${preferredIndex}"]`);
      if (byIndex) return byIndex;
    }

    return (
      contents.querySelector('.gm-tab-content.gm-active') ||
      tabsRoot.querySelector('.gm-tab-content.gm-active')
    );
  }

  function syncTabs(tabsRoot) {
    if (!tabsRoot || !document.contains(tabsRoot)) return;

    const links = tabsRoot.querySelector('.gm-tab-links');
    const contents = tabsRoot.querySelector('.gm-tab-contents');
    if (!links || !contents) return;

    if (!isMobile()) {
      restoreAllPanels(tabsRoot);
      return;
    }

    const activeLink = findActiveLink(links);
    const activeIndex = activeLink?.getAttribute('data-tab-index');
    const activePanel = findActivePanel(tabsRoot, contents, activeIndex);

    // Put non-active nested panels back
    links.querySelectorAll(':scope > .gm-tab-content').forEach((panel) => {
      const idx = panel.getAttribute('data-tab-index');
      if (!activePanel || panel !== activePanel || idx !== activeIndex) {
        restorePanel(panel);
      }
    });

    if (!activeLink || !activePanel) return;

    placePanelAfterLink(activeLink, activePanel);
  }

  function syncAll() {
    getTabsRoots().forEach(syncTabs);
  }

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(() => {
      syncScheduled = false;
      syncAll();
      setTimeout(syncAll, 30);
      setTimeout(syncAll, 120);
    });
  }

  function onInteraction(evt) {
    if (!evt.target?.closest) return;
    if (!evt.target.closest('.gm-submenu.gm-aliexpress')) return;
    scheduleSync();
  }

  function observeMenus() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          scheduleSync();
          return;
        }
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const el = mutation.target;
          if (
            el.classList?.contains('gm-item') ||
            el.classList?.contains('gm-tab-content') ||
            el.classList?.contains('gm-menu') ||
            el.classList?.contains('gm-submenu') ||
            el.classList?.contains('main-menu__disclosure')
          ) {
            scheduleSync();
            return;
          }
        }
      }
    });

    const watch = (root) => {
      if (!root || root.dataset.gmTabsFixObserved === '1') return;
      root.dataset.gmTabsFixObserved = '1';
      observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    };

    watch(document.body);
    document.querySelectorAll('.gm-menu, main-menu, .main-menu__content').forEach(watch);
    setTimeout(() => {
      document.querySelectorAll('.gm-menu, main-menu, .main-menu__content').forEach(watch);
      scheduleSync();
    }, 1000);
    setTimeout(scheduleSync, 2500);
  }

  function init() {
    document.addEventListener('click', onInteraction, true);
    document.addEventListener('touchend', onInteraction, true);

    const onMq = () => {
      if (!isMobile()) restoreAllPanels();
      scheduleSync();
    };

    if (typeof MOBILE_MQ.addEventListener === 'function') {
      MOBILE_MQ.addEventListener('change', onMq);
    } else if (typeof MOBILE_MQ.addListener === 'function') {
      MOBILE_MQ.addListener(onMq);
    }

    observeMenus();
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
